<template>
  <BaseDialog
    :title="$t('lumia.lesson.startDialog.title')"
    :is-open="isOpen"
    type="responsive"
    :align-dismiss-button-right="true"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-6 pb-2">
      <BaseInputField
        v-model="title"
        type="text"
        :label="$t('common.title')"
        class="bg-surface-container-high"
      />
      <div class="flex flex-col gap-2">
        <div>
          <h3 class="text-title-small">{{ $t('lumia.lesson.startDialog.teams') }}</h3>
          <p class="text-body-medium text-on-surface-variant">
            {{ $t('lumia.lesson.startDialog.teamsHint') }}
          </p>
        </div>
        <p v-if="teams.length === 0" class="text-body-medium text-on-surface-variant">
          {{ $t('lumia.lesson.startDialog.noTeams') }}
        </p>
        <ul v-else class="flex flex-col gap-0.5">
          <li
            v-for="team in teams"
            :key="team.id"
            class="first:[&>button]:rounded-t-large! last:[&>button]:rounded-b-large!"
          >
            <BaseCheckboxListItem
              :title="team.name"
              :selected="isTeamSelected(team.id)"
              @click="toggleTeam(team.id)"
            >
              <template #leading>
                <BaseTeamAvatar :name="team.name" size="32" />
              </template>
            </BaseCheckboxListItem>
          </li>
        </ul>
      </div>
    </div>
    <template #dismiss>
      <BaseTextButton size="M" color="text" @click="emit('close')">{{
        $t('common.cancel')
      }}</BaseTextButton>
    </template>
    <template #confirm>
      <BaseTextButton
        size="M"
        color="filled"
        :disabled="!canSubmit || isSubmitting"
        @click="submit"
      >
        {{ $t('common.start') }}
      </BaseTextButton>
    </template>
  </BaseDialog>
</template>

<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'
  import BaseCheckboxListItem from '@/core/components/base/list-item/BaseCheckboxListItem.vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import BaseInputField from '@/core/components/base/form-fields/BaseInputField.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import { ApiRequestStatus } from '@/core/models/api/api-request-status'
  import { useTeamsStore } from '@/stores/teams.store'
  import { useStartLesson } from '../composables/use-start-lesson'
  import BaseTeamAvatar from '@/core/components/base/avatar/BaseTeamAvatar.vue'

  interface Props {
    isOpen: boolean
    lessonImpulseId: string
    defaultTitle: string
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    close: []
    started: [lessonId: string]
  }>()

  const teamStore = useTeamsStore()
  const { startLesson, status } = useStartLesson()

  const title = ref<string>()
  const teamIds = ref<string[]>([])
  const memberIds = ref<string[]>([])

  const teams = computed(() =>
    [...teamStore.allTeams].sort((left, right) => left.name.localeCompare(right.name, 'de')),
  )
  const canSubmit = computed(() => teamIds.value.length > 0 || memberIds.value.length > 0)
  const isSubmitting = computed(() => status.value === ApiRequestStatus.Loading)

  const isTeamSelected = (teamId: string) => teamIds.value.includes(teamId)

  const toggleTeam = (teamId: string) => {
    if (isTeamSelected(teamId)) {
      teamIds.value = teamIds.value.filter((id) => id !== teamId)
      return
    }
    teamIds.value = [...teamIds.value, teamId]
  }

  watch(
    () => props.isOpen,
    (open) => {
      if (!open) {
        return
      }
      title.value = props.defaultTitle
      teamIds.value = []
      memberIds.value = []
    },
  )

  const submit = async () => {
    if (!canSubmit.value || isSubmitting.value) {
      return
    }
    const lessonId = await startLesson({
      lessonImpulseId: props.lessonImpulseId,
      title: title.value,
      teamIds: teamIds.value,
      memberIds: memberIds.value,
    })
    if (lessonId) {
      emit('started', lessonId)
    }
  }
</script>
